import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import EmptyState from "components/ui/empty_state/EmptyState";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { UseBidSupplierBuyerNoticeSummary } from "lib/hooks/api/bid/bids/useBid";
import { UseLeadSupplierBuyerNoticeSummary } from "lib/hooks/api/leads/useLead";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { CoreNotice } from "../types";
import { getNoticeBuyerData } from "../utils";
import ChartCard from "./ChartCard";
import {
  DEFAULT_ALL_PARTNERS_TOKEN,
  EMPTY_STATE_CONFIG,
  getPartnersCardTitle,
  getSimilarNoticeSuppliers,
} from "./utils";

function SupplierRelationshipCharts({
  isLoading,
  savedNotices,
  similarNotices,
  incumbentSupplierIds,
  objectId,
  queryProvider,
}: {
  isLoading: boolean;
  savedNotices: CoreNotice[];
  similarNotices: CoreNotice[];
  incumbentSupplierIds: string[];
  objectId: string;
  queryProvider: UseLeadSupplierBuyerNoticeSummary | UseBidSupplierBuyerNoticeSummary;
}) {
  const { data: signals } = useSignalSettings();
  const { data: partnerSummaries, isLoading: isPartnerSummaryLoading } = queryProvider(objectId, {
    suppliers: DEFAULT_ALL_PARTNERS_TOKEN,
  });

  const noticeBuyerData = getNoticeBuyerData(
    savedNotices.map((notice) => ({
      procurementStageId: notice.procurementStage.id,
      publishDate: notice.publishDate ?? null,
      buyers: (notice.buyers ?? []).map((buyer) => ({
        guid: buyer.guid,
        name: buyer.name ?? "",
        createdAt: buyer.createdAt ?? null,
      })),
    })),
  );

  if (!noticeBuyerData) {
    return null;
  }

  const partnersWithSummaryData = partnerSummaries?.filter(
    (summary) => summary.allAwards.totalCount > 0 || summary.similarAwards.totalCount > 0,
  );
  const partnersIdentifiedCount = partnersWithSummaryData?.length ?? 0;
  const partnersTitle = isPartnerSummaryLoading ? (
    <SkeletonText width="255px" fontSize={25} />
  ) : (
    getPartnersCardTitle(partnersIdentifiedCount)
  );

  const { similarNoticeIds, allSupplierIds } = getSimilarNoticeSuppliers(
    savedNotices,
    similarNotices,
  );

  const hasSupplierAwards = allSupplierIds.length > 0;
  const hasPartnerSignals = (signals?.partners ?? []).length > 0;
  const showPartnersEmptyState =
    !isPartnerSummaryLoading && (!hasPartnerSignals || partnersIdentifiedCount === 0);

  return (
    <Flex column gap={16}>
      <ChartCard
        title="Contract awards for Suppliers who have worked with this buyer"
        chartType="allSuppliers"
        defaultBuyer={noticeBuyerData.buyer}
        defaultSupplierIds={allSupplierIds}
        objectId={objectId}
        similarNoticeIds={similarNoticeIds}
        incumbentSupplierIds={incumbentSupplierIds}
        emptyState={hasSupplierAwards ? null : <SuppliersEmptyState stateKey="allSuppliers" />}
        isLoading={isLoading}
        queryProvider={queryProvider}
      />
      <ChartCard
        title={partnersTitle}
        chartType="partners"
        defaultBuyer={noticeBuyerData.buyer}
        defaultSupplierIds={DEFAULT_ALL_PARTNERS_TOKEN}
        objectId={objectId}
        similarNoticeIds={similarNoticeIds}
        incumbentSupplierIds={incumbentSupplierIds}
        emptyState={
          showPartnersEmptyState ? (
            <PartnerEmptyState
              hasPartnerSignals={hasPartnerSignals}
              partnersIdentifiedCount={partnersIdentifiedCount}
            />
          ) : null
        }
        isLoading={isLoading || isPartnerSummaryLoading}
        queryProvider={queryProvider}
      />
    </Flex>
  );
}

function SuppliersEmptyState({ stateKey }: { stateKey: string }) {
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { heading, description, action } = EMPTY_STATE_CONFIG[stateKey];

  return (
    <EmptyStateWrapper>
      <EmptyState
        type="noResults"
        size="large"
        heading={heading}
        description={description}
        action={isRelevancySearchEnabled ? undefined : action}
      />
    </EmptyStateWrapper>
  );
}

function PartnerEmptyState({
  hasPartnerSignals,
  partnersIdentifiedCount,
}: {
  hasPartnerSignals: boolean;
  partnersIdentifiedCount: number;
}) {
  if (!hasPartnerSignals) {
    return <SuppliersEmptyState stateKey="noPartnerSignals" />;
  }
  if (partnersIdentifiedCount === 0) {
    return <SuppliersEmptyState stateKey="noPartnerRelationships" />;
  }
  return null;
}

const EmptyStateWrapper = styled(Flex)({
  textAlign: "center",
});

export default SupplierRelationshipCharts;
